<template>
    <div :key="data.widgetID">
        <data-table
            :widget="data"
            :border="border"
            :can-sort-rows="canSortRows"
            :cell-class-name="getCellClassName"
            :is-multi-queue-table="true"
            :columns="getAvailableColumns"
            :editable="editable"
            :row-style="getRowStyle"
            :displayQueueAsRows="displayQueueAsRows"
            :showColumns="getVisibleColumns"
            :columnsWithPercentage="getColumnsWithPercentage"
            :stripe="stripe"
            :tableData="fetchTableData"
            :widgetTitle="data.Title"
            @sort-change="onSortChange"
            @on-reorder-rows="onReorderRows"
            @on-update-layout="onUpdateLayout">
            <template v-if="displayQueueAsRows" v-slot:header_title="{column}">
                <el-tooltip :content="getQueueName(column.prop)" :open-delay="300" placement="top">
                        <span class="font-medium uppercase">
                            {{ getQueueName(column.prop) }}
                        </span>
                </el-tooltip>
            </template>
            <template v-slot:Callers="{row, index}">
                <caller-count :key="index" :queueID="row.queue_id" :queueName="getQueueName(row.queue_id)" @getCallerCount="collectCallerCount" />
            </template>
            <template v-slot:CurrentWaitTime="{row, index}">
                <max-wait-time :key="index" :queueID="row.queue_id" :queueName="getQueueName(row.queue_id)" @getMaxWaitTime="collectCurrentWaitTime" />
            </template>
            <template v-slot:queue_id="{row}">
                {{ getQueueName(row.queue_id) }}
            </template>
            <template v-slot:All="{row, index}">
                <div v-if="isMaxWaitTimeRealTimeRow(row)">
                    <max-wait-time :key="index" :queueID="queueWithOldestCall"/>
                </div>
                <div v-else>
                    {{ getQueueTotals(row) }}
                </div>
            </template>
            <template v-slot:realTimeCell="{column, row, index}">
                <div v-if="isCallersRealTimeCell(column, row)">
                    <caller-count :key="index" :queueID="getColumnQueueID(column)"/>
                </div>
                <div v-if="isMaxWaitTimeRealTimeCell(column, row)">
                    <max-wait-time :key="index" :queueID="getColumnQueueID(column)"/>
                </div>
            </template>
            <template v-slot:title>
                <base-widget-title :title="data.Title"/>
            </template>
            <template v-slot:time-frame>
                <span class="px-4 flex items-center">
                    <time-frame :widget="data"/>
                </span>
            </template>
            <template v-slot:search-input>
                <div class="flex items-center w-48 px-1">
                    <el-input
                        clearable
                        :placeholder="$t('general.search')"
                        size="small"
                        v-model="filter">
                        <i slot="prefix" class="el-input__icon vc-icon-search icon-md text-primary ml-1" />
                    </el-input>
                </div>
            </template>
            <template v-for="slot in Object.keys(queueDashboardColumnStyles)" v-slot:[slot]="{row}">
                <div :key="slot" :style="getDynamicColorForColumn(slot)">
                    {{ row[slot] }}
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
    import get from 'lodash/get'
    import cloneDeep from 'lodash/cloneDeep'
    import { Option, Pagination, Select, Tooltip } from 'element-ui'
    import {
        defaultVisibleColumns,
        defaultAvailableColumns,
        formatQueueDashboardsData,
        queueDashboardColumnStyles,
    } from '@/helpers/multiQueueDashboard'
    import minBy from 'lodash/minBy'
    import dataTableMixin from '@/mixins/dataTableMixin'
    import { getOptionsList } from '@/helpers/entitiesList'
    import { mapOrder } from '@/helpers/util'
    import {timeFormatter} from "@/helpers/timeFormatter";
    import queueMixin from "@/mixins/queueMixin";

    export default {
        name: 'queues-table',
        mixins: [dataTableMixin, queueMixin],
        components: {
            CallerCount: () => import('./CallerCount'),
            MaxWaitTime: () => import('./MaxWaitTime'),
            DataTable: () => import('@/components/Table/DataTable'),
            TimeFrame: () => import('../Table/TimeFrame'),
            [Select.name]: Select,
            [Option.name]: Option,
            [Tooltip.name]: Tooltip,
            [Pagination.name]: Pagination,
        },
        props: {
            data: {
                type: Object,
                default: () => ({}),
            },
            editable: {
                type: Boolean,
                default: false,
            },
            queuesData: {
                type: Array,
                default: [],
            },
            tableConfigs: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                canSortRows: 'custom',
                tableData: [],
                columns: [],
                searchableFields: [],
                filter: '',
                border: true,
                stripe: false,
                widget: {},
                QUEUE_LIST_KEY: '{|queue_list|}',
                billingCdrQueueTypes: [
                    'Answer',
                    'Abandoned',
                    'IVRExit',
                    'PickUp',
                    'TimeOutExit',
                    'JoinEmpty',
                    'LeaveEmpty',
                    'JoinUnavail',
                    'LeaveUnavail',
                    'Full',
                    'NextDestination',
                ],
                queueDashboardColumnStyles,
                callerCountData: {},
                currentWaitTimeData: {},
                waiting_state: '',
                callers_state: '',
                queueName: ''
            }
        },
        computed: {
            fetchTableData: {
                get: function () {
                    let tableData = this.tableData
                    tableData.map(el => el.q_name = this.getQueueName(el.queue_id))

                    if (this.displayQueueAsRows) {
                        let visibleRows = this.columnsAreManaged ? this.visibleColumns : defaultVisibleColumns
                        tableData = tableData.filter(c => visibleRows.includes(c['Stat type']))
                        tableData = mapOrder(tableData, visibleRows, 'Stat type')
                    }

                    if (this.filter && this.searchableFields.length > 0) {
                        tableData = tableData.filter(c => {
                            return this.searchableFields.some(field => {
                                if (c[field]) {
                                    return c[field].toString().toLowerCase().includes(this.filter.toLowerCase())
                                }
                                return false
                            })
                        })
                    }
                    const visibleQueuesColumns = get(this.widget.WidgetLayout, 'Columns.visibleQueuesColumns') || this.columns.map(c => c.prop)
                    tableData = mapOrder(tableData, visibleQueuesColumns, 'queue_id')

                    return tableData
                },
                set: function (newValue) {
                    return newValue
                }
            },
            allEntityQueues() {
                return getOptionsList(`${this.QUEUE_LIST_KEY}`)
            },
            displayQueueAsRows() {
                return !get(this.data.WidgetLayout, 'displayQueuesAsRow', true)
            },
            columnsAreManaged() {
                return !!get(this.widget.WidgetLayout, 'Columns.visibleColumns')
            },
            getAvailableColumns() {
                if (this.displayQueueAsRows) {
                    return get(this.widget.WidgetLayout, 'Columns.availableQueuesColumns') || this.columns
                }

                return this.availableColumns
            },
            getVisibleColumns() {
                if (!this.displayQueueAsRows) {
                    return this.columnsAreManaged ? this.visibleColumns : defaultVisibleColumns
                }

                return get(this.widget.WidgetLayout, 'Columns.visibleQueuesColumns') || this.columns.map(c => c.prop)
            },
            selectedQueues() {
                try {
                    const queueListConfig = this.data.WidgetConfig.filter((config) => config.ParameterName === this.QUEUE_LIST_KEY)
                    return get(queueListConfig, '[0].WidgetParameterValueJson.EntityPositive', [])
                } catch (e) {
                    return []
                }
            },
            queueWithActiveCalls() {
                return this.$store.getters['queues/queueWithActiveCalls']
            },
            queueWithOldestCall() {
                const allCalls = []
                this.queueWithActiveCalls.forEach((queue) => {
                    let calls = queue.Calls || []
                    calls.map((call) => {
                        call['QueueID'] = queue.QueueID
                    })
                    allCalls.push(...calls)
                })
                let oldestCall = minBy(allCalls, 'JoinTimeStamp')
                return get(oldestCall, 'QueueID', null)
            },
            getColumnsWithPercentage() {
                if (!this.showStatsInPercentage && !this.displayQueueAsRows) {
                    return []
                }
                return this.billingCdrQueueTypes
            },
            allQueueCalls () {
                return this.$store.getters['queues/allQueueCalls']
            },
            callersValue () {
                if (!this.callerCountData) return
                const callersArraySorted = Object.entries(this.callerCountData).sort((a, b) => Number(a[1]) - Number(b[1]))

                if (!callersArraySorted) return
                const lastItem = callersArraySorted.pop()
                if (!lastItem) return
                return {
                    value: lastItem[1],
                    queueID: lastItem[0]
                }

            },
            maxRingTimeValue () {
                if (!this.currentWaitTimeData) return
                const currentWaitTimeDataSorted = Object.entries(this.currentWaitTimeData).sort((a, b) => this.timeToSeconds(a[1]) - this.timeToSeconds(b[1]))
                if (!currentWaitTimeDataSorted) return
                const lastItem = currentWaitTimeDataSorted.pop()
                if (!lastItem) return
                return {
                    value: this.timeToSeconds(lastItem[1]),
                    queueID: lastItem[0]
                }
            },
            isMaximumWarningCallersValue() {
                const max = this.callersValue
                if (!max) return
                const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningCallersValue', 0)
                const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitCallersValue', 0)

                return Number(max.value) <= Number(maxValFromSettings) && Number(max.value) >= Number(minValFromSettings)
            },
            isMaximumLimitCallersValue() {
                const max = this.callersValue
                if (!max) return
                const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitCallersValue', 0)

                return Number(max.value) >= Number(maxValFromSettings)
            },
            isMaximumWarningMaxRingTimeValue() {
                const max = this.maxRingTimeValue
                if (!max) return
                const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningMaxRingTimeValue', 0)
                const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeValue', 0)

                return Number(max.value) <= Number(maxValFromSettings) && Number(max.value) >= Number(minValFromSettings)
            },
            isMaximumLimitMaxRingTimeValue() {
                const max = this.maxRingTimeValue
                if (!max) return
                const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeValue', 0)

                return Number(max.value) >= Number(maxValFromSettings)
            },
            warningSounds () {
                return this.$store.getters['entities/getWarningSounds']
            },
            errorSounds () {
                return this.$store.getters['entities/getErrorSounds']
            },
            allSounds () {
                return this.$store.getters['entities/getAllSounds']
            },
            filteredQueue () {
                if (this.selectedQueues.length) {
                    return this.allEntityQueues.filter((queue) => this.selectedQueues.includes(queue.queue_id))
                } else {
                    return this.allEntityQueues
                }
            }
        },
        methods: {
            getQueueName(queueID) {
                if (queueID === 'All' || queueID === 'Stat type') {
                    return queueID
                }

                let queue = this.allEntityQueues.filter((queue) => queue.queue_id === Number(queueID))
                return get(queue, '[0].q_name', '--')
            },
            getRowStyle({ row }) {
                let color = get(queueDashboardColumnStyles[row['Stat type']], 'color')
                const maximumWarningCallersColor = get(this.data.WidgetLayout, 'maximumWarningCallersColor', '')
                const maximumLimitCallersColor = get(this.data.WidgetLayout, 'maximumLimitCallersColor', '')
                const maximumWarningMaxRingTimeColor = get(this.data.WidgetLayout, 'maximumWarningMaxRingTimeColor', '')
                const maximumLimitMaxRingTimeColor = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeColor', '')

                return {
                    '--text-col-color': color,
                    ...(maximumWarningCallersColor && {'--maximum-warning-callers-color': maximumWarningCallersColor}),
                    ...(maximumLimitCallersColor && {'--maximum-limit-callers-color': maximumLimitCallersColor}),
                    ...(maximumWarningMaxRingTimeColor && {'--maximum-warning-max-ring-time-color': maximumWarningMaxRingTimeColor}),
                    ...(maximumLimitMaxRingTimeColor && {'--maximum-limit-max-ring-time-color': maximumLimitMaxRingTimeColor})
                }
            },
            getCellClassName({ column, row }) {
                let className = ''
                if (this.callersValue && column.property === 'Callers') {
                    const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningCallersValue', 0)
                    const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitCallersValue', 0)
                    const val = this.callerCountData[row.queue_id]

                    if (Number(val) >= Number(maxValFromSettings)) {
                        className = 'limitCallersValue'
                    } else if (Number(val) >= Number(minValFromSettings)) {
                        className = 'warningCallersValue'
                    }

                    return className
                }

                if (this.maxRingTimeValue && column.property === 'CurrentWaitTime' && this.currentWaitTimeData[row.queue_id] !== undefined) {
                    const val = this.timeToSeconds(this.currentWaitTimeData[row.queue_id])
                    const minValFromSettings = get(this.data.WidgetLayout, 'maximumWarningMaxRingTimeValue', 0)
                    const maxValFromSettings = get(this.data.WidgetLayout, 'maximumLimitMaxRingTimeValue', 0)

                    if (Number(val) >= Number(maxValFromSettings)) {
                        className = 'limitMaxRingTimeValue'
                    } else if (Number(val) >= Number(minValFromSettings)) {
                        className = 'warningMaxRingTimeValue'
                    }
                    return className
                }

                if (queueDashboardColumnStyles[column.property] || queueDashboardColumnStyles[row['Stat type']]) {
                    className = 'text-white'
                }

                return className
            },
            async getWidgetData() {
                try {
                    const selectedEntityQueues = this.filteredQueue.map((queue) => queue.queue_id)

                    if (!selectedEntityQueues.length) {
                        return
                    }

                    let availableColumns = this.queuesData[0]

                    let data = [...this.queuesData]
                    let columns = []

                    let displayRowWithTotals = get(this.data.WidgetLayout, 'displayRowWithTotals', true)
                    let displayQueueAsRows = this.displayQueueAsRows

                    const showStatsInPercentage = this.showStatsInPercentage

                    let queueIDsFromSocket = data.map((queue) => queue.queue_id)

                    this.$store.dispatch('sound/removeSoundsFromQueue', `multi-queue-${this.queueName}`)

                    selectedEntityQueues.forEach((queueID) => {
                        if (queueIDsFromSocket.includes(queueID)) {
                            return
                        }
                        let objectToAppend = {
                            'AvgRingTime': 0,
                            'CallCount': 0,
                            'ExitCounts': [],
                            'InSLACount': 0,
                            'MaxRingTime': 0,
                            'NotInSLACount': 0,
                            'queue_id': Number(queueID),
                            'q_name': ''
                        }
                        data.push(objectToAppend)
                    })

                    let result = formatQueueDashboardsData(data, displayRowWithTotals, displayQueueAsRows, showStatsInPercentage)

                    availableColumns = result.columns
                    data = result.data

                    for (let column in availableColumns) {
                        const columnData = {
                            prop: column,
                            fixed: false,
                            align: 'center',
                            minWidth: 130,
                            label: this.$t(column) || startCase(column),
                        }

                        columns.push(columnData)
                        this.searchableFields.push(column)
                    }
                    this.searchableFields.push('q_name')

                    this.columns = columns
                    this.tableData = data

                } catch (e) {
                    console.warn(e)
                }
            },
            isCallersRealTimeCell(column, row) {
                if (['All', 'Stat type'].includes(column.prop)) {
                    return
                }
                return row['Stat type'] === 'Callers'
            },
            isMaxWaitTimeRealTimeCell(column, row) {
                if (['All', 'Stat type'].includes(column.prop)) {
                    return
                }
                return row['Stat type'] === 'CurrentWaitTime'
            },
            getColumnQueueID(column) {
                return Number(column.prop)
            },
            isMaxWaitTimeRealTimeRow(row) {
                return row['Stat type'] === 'CurrentWaitTime'
            },
            getQueueTotals(row) {
                if (row['Stat type'] === 'Callers') {
                    return this.allQueueCalls.length
                }
                return row['All']
            },
            onReorderRows(data = {}) {
                let { newIndex, oldIndex } = data
                let payload = {}
                if (this.displayQueueAsRows) {
                    const availableColumns = get(this.widget.WidgetLayout, 'Columns.availableColumns') || defaultAvailableColumns
                    const visibleColumns = get(this.widget.WidgetLayout, 'Columns.visibleColumns') || defaultVisibleColumns
                    const queueIdIndex = visibleColumns.findIndex(el => el === 'queue_id')
                    const targetIndex = visibleColumns[oldIndex]
                    const targetPayload = availableColumns.find(el => el.prop === targetIndex)

                    if (targetPayload) {
                        availableColumns.splice(oldIndex, 1)
                        availableColumns.splice(newIndex, 0, targetPayload)
                    }
                    visibleColumns.splice(oldIndex, 1)
                    visibleColumns.splice(newIndex, 0, targetIndex)
                    visibleColumns.splice(queueIdIndex, 0, 'queue_id')
                    payload = {
                        availableColumns,
                        visibleColumns,
                    }
                } else {
                    const availableQueuesColumns = get(this.widget.WidgetLayout, 'Columns.availableQueuesColumns') || this.columns
                    const visibleQueuesColumns = get(this.widget.WidgetLayout, 'Columns.visibleQueuesColumns') || this.columns.map(c => c.prop)
                    const statTypeIndex = visibleQueuesColumns.findIndex(el => el === 'Stat type')
                    visibleQueuesColumns.splice(statTypeIndex, 1)
                    const targetIndex = visibleQueuesColumns[oldIndex]

                    const targetPayload = availableQueuesColumns.find(el => el.prop === targetIndex)

                    if (targetPayload) {
                        availableQueuesColumns.splice(oldIndex, 1)
                        availableQueuesColumns.splice(newIndex, 0, targetPayload)
                    }
                    visibleQueuesColumns.splice(oldIndex, 1)
                    visibleQueuesColumns.splice(newIndex, 0, targetIndex)
                    visibleQueuesColumns.splice(statTypeIndex, 0, 'Stat type')

                    payload = {
                        availableQueuesColumns,
                        visibleQueuesColumns,
                    }
                }
                this.onUpdateLayout(payload)
            },
            onSortChange({column, prop, order}) {
                if (prop === null) {
                    return
                }

                if (order === 'ascending') {
                    this.fetchTableData = this.fetchTableData.sort((a, b) => (a[prop] > b[prop]) ? 1 : -1)
                } else if (order === 'descending') {
                    this.fetchTableData = this.fetchTableData.sort((a, b) => (a[prop] < b[prop]) ? 1 : -1)
                }
            },
            getDynamicColorForColumn(slot) {
                const nameOfSlot = slot.charAt(0).toLowerCase() + slot.slice(1)
                const color = this.data.WidgetLayout.colors && nameOfSlot in this.data.WidgetLayout.colors && this.data.WidgetLayout.colors[nameOfSlot] ? this.data.WidgetLayout.colors[nameOfSlot] : queueDashboardColumnStyles[slot].color
                return {
                    color: color
                }
            },
            timeToSeconds(time) {
                const [minutes, seconds] = time.split(':');
                const timeSeconds = (parseInt(minutes) * 60) + parseInt(seconds)
                return timeSeconds
            },
            soundAnimation (type, value) {
                if ((value && Number(value) === 0) || value === undefined) {
                    this.$store.dispatch('sound/removeOldSoundFromQueue',{
                            id: `multi-queue-${this.queueName}`,
                            type: type
                        })
                    return
                }

                if (!this.widget || !this.widget.WidgetLayout) {
                    return
                }

                let soundItem = null

                if (type === 'caller_warning') {
                    const notificationData = {
                        notification_value: this.widget.WidgetLayout.maximumWarningCallersValue,
                        entity_name: this.queueName,
                        current_value: this.callersValue.value,
                        property: 'max warning callers'
                    }
                    soundItem = {
                        id: `multi-queue-${this.queueName}-caller_warning`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumWarningCallersSound, 'warning') || '',
                        played: false,
                        isPlaying: false,
                        type: 'info',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.multiQueueTableHtml', {...notificationData})
                    }
                }
                if (type === 'caller_limit') {
                    const notificationData = {
                        notification_value: this.widget.WidgetLayout.maximumLimitCallersValue,
                        entity_name: this.queueName,
                        current_value: this.callersValue.value,
                        type: 'limit',
                        property: 'max limit callers'
                    }
                    soundItem = {
                        id: `multi-queue-${this.queueName}-caller_limit`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumLimitCallersSound, 'limit') || '',
                        played: false,
                        isPlaying: false,
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.multiQueueTableHtml', {...notificationData})
                    }
                }
                if (type === 'wait_warning') {
                    const notificationData = {
                        notification_value: timeFormatter(this.widget.WidgetLayout.maximumWarningMaxRingTimeValue),
                        entity_name: this.queueName,
                        current_value: timeFormatter(this.maxRingTimeValue.value),
                        property: 'max ring time warning'
                    }
                    soundItem = {
                        id: `multi-queue-${this.queueName}-wait_warning`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumWarningMaxRingTimeSound, 'warning') || '',
                        played: false,
                        isPlaying: false,
                        type: 'info',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.multiQueueTableHtml', {...notificationData})
                    }
                }
                if (type === 'wait_limit') {
                    const notificationData = {
                        notification_value: timeFormatter(this.widget.WidgetLayout.maximumLimitMaxRingTimeValue),
                        entity_name: this.queueName,
                        current_value: timeFormatter(this.maxRingTimeValue.value),
                        property: 'max ring time limit'
                    }
                    soundItem = {
                        id: `multi-queue-${this.queueName}-wait_limit`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.maximumLimitMaxRingTimeSound, 'limit') || '',
                        played: false,
                        isPlaying: false,
                        type: 'limit',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.multiQueueTableHtml', {...notificationData})
                    }
                }
                if (this.widget.WidgetLayout.backQueueSLASound !== 'none' && type === 'idle' && this.waiting_state === 'idle'  && this.callers_state === 'idle') {
                    soundItem = {
                        id: `multi-queue-${this.queueName}-idle`,
                        sound: this.getCurrentSoundValue(this.widget.WidgetLayout.backQueueSLASound, 'idle') || '',
                        played: false,
                        isPlaying: false,
                        type: 'idle',
                        timestamp: new Date().getTime(),
                        notificationHTML: this.$t('widget.allMultiQueueBackToIdle')
                    }
                }

                if (soundItem && soundItem.sound) {
                    this.$store.dispatch('sound/setupSounds', soundItem)
                }
            },
            collectCallerCount({queueID, value, queueName}) {
                this.callerCountData = {
                    ...this.callerCountData,
                    [queueID]: value
                }
                if (this.queueName !== queueName) {
                    this.queueName = queueName
                }
                if (value === 0) {
                    this.$store.dispatch('sound/removeSoundsFromQueue', `multi-queue-${this.queueName}`)
                }
            },
            collectCurrentWaitTime ({queueID, value, queueName}) {
                this.currentWaitTimeData = {
                    ...this.currentWaitTimeData,
                    [queueID]: value
                }
                if (this.queueName !== queueName) {
                    this.queueName = queueName
                }
                if (value === 0) {
                    this.$store.dispatch('sound/removeSoundsFromQueue', `multi-queue-${this.queueName}`)
                }
            },
            getCurrentSoundValue (soundValue, type) {
                if (!soundValue) return
                let sounds = ''
                if (type === 'idle') {
                    sounds = this.allSounds
                } else if (type === 'warning') {
                    sounds = this.warningSounds
                } else {
                    sounds = this.errorSounds
                }
                if (!sounds) null
                const currentSound = sounds.find(el => el.value === soundValue)
                if (!currentSound) {
                    return null
                }
                return currentSound.sound
            },
        },
        mounted() {
            if (!this.data.WidgetLayout.hasOwnProperty('displayQueuesAsRow')) {
                this.$set(this.data.WidgetLayout, 'displayQueuesAsRow', true)
            }
            if (!this.data.WidgetLayout.hasOwnProperty('displayRowWithTotals')) {
                this.$set(this.data.WidgetLayout, 'displayRowWithTotals', true)
            }
            if (!this.data.WidgetLayout.hasOwnProperty('showStatsInPercentage')) {
                this.$set(this.data.WidgetLayout, 'showStatsInPercentage', true)
            }
        },
        watch: {
            data: {
                immediate: true,
                deep: true,
                handler(data) {
                    this.widget = cloneDeep(data)
                    this.getWidgetData()
                },
            },
            queuesData: {
                immediate: true,
                deep: true,
                handler() {
                    this.getWidgetData()
                },
            },
            isMaximumWarningCallersValue: {
                immediate: true,
                handler (val) {
                    if (val === undefined) return
                    if (val) {
                        this.callers_state = 'warning'
                    } else if (!val) {
                        this.callers_state = 'idle'
                    }
                }
            },
            isMaximumLimitCallersValue: {
                immediate: true,
                handler (val) {
                    if (val === undefined) return
                    if (val) {
                        this.callers_state = 'limit'
                    } else if (!val) {
                        this.callers_state = 'idle'
                    }
                }
            },
            isMaximumWarningMaxRingTimeValue: {
                immediate: true,
                handler (val) {
                    if (val === undefined) return
                    if (val) {
                        this.waiting_state = 'warning'
                    } else if (!val) {
                        this.waiting_state = 'idle'
                    }
                }
            },
            isMaximumLimitMaxRingTimeValue: {
                immediate: true,
                handler (val) {
                    if (val === undefined) return
                    if (val) {
                        this.waiting_state = 'limit'
                    } else if (!val) {
                        this.waiting_state = 'idle'
                    }
                }
            },
            callers_state: {
                immediate: true,
                handler (newVal, oldVal) {
                    const max = this.callersValue
                    if (!max) return
                    if (oldVal !== undefined && oldVal !== '' && newVal === 'idle' && this.waiting_state === 'idle') {
                        this.soundAnimation('idle')
                    }
                    if (newVal && newVal === 'limit') {
                        this.soundAnimation('caller_limit', max.value)
                    }
                    if (newVal && newVal === 'warning') {
                        this.soundAnimation('caller_warning', max.value)
                    }
                }
            },
            waiting_state: {
                immediate: true,
                handler (newVal, oldVal) {
                    const max = this.maxRingTimeValue
                    if (!max) return
                    if (oldVal !== undefined && oldVal !== '' && newVal === 'idle' && this.callers_state === 'idle') {
                        this.soundAnimation('idle')
                    }
                    if (newVal && newVal === 'limit') {
                        this.soundAnimation('wait_limit', max.value)
                    }
                    if (newVal && newVal === 'warning') {
                        this.soundAnimation('wait_warning', max.value)
                    }
                }
            }
        },
        beforeDestroy() {
            this.$store.dispatch('sound/removeSoundsFromQueue', `multi-queue-${this.queueName}`)
        },
    }
</script>
<style lang="scss">
td.text-white > .cell {
    color: white;
}
</style>
<style lang="scss" scoped>
::v-deep .el-input__inner::placeholder {
    @apply text-gray-500 font-medium text-base pl-1;
}
::v-deep .el-input__inner {
    @apply text-black font-medium text-base;
}
::v-deep .el-table .el-table__cell {
    padding: 6px 0px;
}
::v-deep .el-table thead th .cell {
    line-height: 15px;
    color: #6e6d6d;
    @apply flex;
}

::v-deep .el-table thead th .cell {
    line-height: 15px;
    color: #6e6d6d;
    @apply flex;
}

::v-deep .el-table .cell div {
    color: var(--text-col-color);
}

@keyframes color_bg_warning_maxRing {
  to { background-color: var(--maximum-warning-max-ring-time-color); }
}

@-webkit-keyframes color_bg_warning_maxRing {
    to { background-color: var(--maximum-warning-max-ring-time-color); }
}

@keyframes color_bg_warning_callers {
  to { background-color: var(--maximum-warning-callers-color); }
}

@-webkit-keyframes color_bg_warning_callers {
    to { background-color: var(--maximum-warning-callers-color); }
}

@keyframes color_bg_limit_maxRing {
  to { background-color: var(--maximum-limit-max-ring-time-color); }
}

@-webkit-keyframes color_bg_limit_maxRing {
    to { background-color: var(--maximum-limit-max-ring-time-color); }
}

@keyframes color_bg_limit_callers {
  to { background-color: var(--maximum-limit-callers-color); }
}

@-webkit-keyframes color_bg_limit_callers {
    to { background-color: var(--maximum-limit-callers-color); }
}

::v-deep .el-table td.el-table__cell.warningMaxRingTimeValue {
    -webkit-animation: color_bg_warning_maxRing 1.5s infinite;
    -moz-animation: color_bg_warning_maxRing 1.5s infinite;
    -ms-animation: color_bg_warning_maxRing 1.5s infinite;
    -o-animation: color_bg_warning_maxRing 1.5s infinite;
    animation: color_bg_warning_maxRing 1.5s infinite;
}
::v-deep .el-table td.el-table__cell.warningCallersValue {
    -webkit-animation: color_bg_warning_callers 1.5s infinite;
    -moz-animation: color_bg_warning_callers 1.5s infinite;
    -ms-animation: color_bg_warning_callers 1.5s infinite;
    -o-animation: color_bg_warning_callers 1.5s infinite;
    animation: color_bg_warning_callers 1.5s infinite;
}
::v-deep .el-table td.el-table__cell.limitMaxRingTimeValue {
    -webkit-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    -moz-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    -ms-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    -o-animation: color_bg_limit_maxRing 1.5s infinite alternate;
    animation: color_bg_limit_maxRing 1.5s infinite alternate;
}
::v-deep .el-table td.el-table__cell.limitCallersValue {
    -webkit-animation: color_bg_limit_callers 1.5s infinite;
    -moz-animation: color_bg_limit_callers 1.5s infinite;
    -ms-animation: color_bg_limit_callers 1.5s infinite;
    -o-animation: color_bg_limit_callers 1.5s infinite;
    animation: color_bg_limit_callers 1.5s infinite;
}
</style>
